import { Popover } from '@material-ui/core'
import React, { MutableRefObject, ReactElement, useContext, useEffect, useRef, useState } from 'react'
import { Subscription } from 'rxjs'
import { takeWhile } from 'rxjs/operators'
import useIsMountedRef from 'use-is-mounted-ref'
import { FieldsTab as FieldsTabLanguage } from '../../../../i18n'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext } from '../../Common/Contexts'
import { ReactState, RptFilterEntityInfo } from '../../Common/Types/Types'
import { mapStatPropToStatisticsConfiguration } from '../../Common/Utils/ReportStatistics'
import { toggleFieldsAddMenuOpen$ } from '../../../NonReact/common/RxJS/ToggleFieldsAddMenuOpen'
import { STATISTICS_SETTINGS_ALARM_CAPABILITY_INDIVIDUALSTATS } from './Constants'
import CustomSettings from './CustomSettings'
import DisplaySettings from './DisplaySettings'
import FieldsList from './FieldsList'
import FieldsListAdd from './FieldsListAdd'
import SortListContent from './SortList'
import StatisticAlarm from './StatisticAlarm'
import { AvailableStatisticsFieldsArray, StatisticsConfiguration, StatProps } from './Types'

// import './fieldsTab.scss'

export type FieldsProps = {
    isWallboard: boolean
    statsList: AvailableStatisticsFieldsArray | undefined
    resetStatisticsConfigurationToReportDefault: () => void
    configuredStatisticsConfigurations: StatisticsConfiguration[]
    setConfiguredStatisticsConfigurations: React.Dispatch<React.SetStateAction<StatisticsConfiguration[]>>
    currentReportStyleAlarmTypes: number
    isReportDisplayedAsChart: boolean
    isReportRealTime: boolean
    currentReportStyleSupportsStatDisplayedAs: boolean
    currentReportStyleSupportsColouredStats: boolean
    currentReportStyleSupportsSortingEver: boolean
    currentReportStyleSupportsSortingRealTime: boolean
    currencyFormatter: CDecimalFormatter
    rptFilterEntityInfo: RptFilterEntityInfo | undefined
    isUserLicensedForCurrentReportStyle: boolean
}

/**
 * Component representing fields tab of report configuration modal window.
 *
 * @param statsList List of statistics (fields) that can be configured for this report.
 * @param resetStatisticsConfigurationToReportDefault List of statistics configurations that are configured by default for this report definition (style).
 * @param configuredStatisticsConfigurations List of statistics configurations that are currently configured for this report.
 * @param setConfiguredStatisticsConfigurations Update method for respective property.
 * @param isWallboard Flag specifying whether current report is considered a wallboard report.
 * @param isReportDisplayedAsChart Flag specifying whether current report should be displayed as chart.
 * @param currentReportStyleAlarmTypes Alarm types for current report's definition (style).
 * @param isReportRealTime Flag specifying whether current report is configured as real-time.
 * @param currentReportStyleSupportsStatDisplayedAs Flag specifying whether current report support displayedAs option.
 * @param currentReportStyleSupportsColouredStats Flag specifying whether current report support coloured statistics option.
 * @param currencyFormatter Currently active currency formatter.
 * @param isUserLicensedForCurrentReportStyle Flag specifying whether user is licensed for current report style.
 * @param currentReportStyleSupportsSortingEver Flag specifying whether report style support sorting of fields under any circumstances.
 * @param currentReportStyleSupportsSortingRealTime Flag specifying whether current report style support sorting of fields even if the report is real-time.
 */
const FieldsTab: React.FC<FieldsProps> = ({
    statsList,
    resetStatisticsConfigurationToReportDefault,
    configuredStatisticsConfigurations,
    setConfiguredStatisticsConfigurations,
    isWallboard,
    isReportDisplayedAsChart,
    currentReportStyleAlarmTypes,
    isReportRealTime,
    currentReportStyleSupportsColouredStats,
    currentReportStyleSupportsStatDisplayedAs,
    currentReportStyleSupportsSortingEver,
    currentReportStyleSupportsSortingRealTime,
    currencyFormatter,
    rptFilterEntityInfo,
    isUserLicensedForCurrentReportStyle
}): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('FieldsTab')
    language.loadBundle('SortList') // TODO: move to component
    const percentageFormatString: string = language?.get(FieldsTabLanguage.PercentageFormatString)

    const [addMenuOpen, setAddMenuOpen]: ReactState<boolean> = useState<boolean>(false)
    const [sortMenuOpen, setSortMenuOpen]: ReactState<boolean> = useState<boolean>(false)
    const popoverRefEl = useRef(null)
    const sortPopoverRef = useRef(null)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const subscriptionForAddMenuOpen: MutableRefObject<Nullable<Subscription>> = useRef<Nullable<Subscription>>()

    const [selectedStatsIDs, setSelectedStatIDs]: ReactState<number[]> = useState<number[]>([])
    const selectedStatisticsConfiguration =
        selectedStatsIDs.length === 1
            ? configuredStatisticsConfigurations.find(
                  (statisticConfiguration: StatisticsConfiguration): boolean =>
                      statisticConfiguration.ID === selectedStatsIDs[0]
              )
            : undefined

    // Determine whether the selected report style supports individual statistics alarms.
    const statisticAlarmsSupported =
        isReportRealTime &&
        (currentReportStyleAlarmTypes & STATISTICS_SETTINGS_ALARM_CAPABILITY_INDIVIDUALSTATS) ===
            STATISTICS_SETTINGS_ALARM_CAPABILITY_INDIVIDUALSTATS

    const addStatistic = (statProp: StatProps): void => {
        const statisticAlreadyPresent = !!configuredStatisticsConfigurations.find(
            (statisticsConfiguration) => statisticsConfiguration.ID === statProp.StatID
        )
        // Only add statistic if it is not already in list.
        if (!statisticAlreadyPresent) {
            const statisticsConfiguration = mapStatPropToStatisticsConfiguration(
                statProp,
                percentageFormatString,
                language,
                currencyFormatter
            )

            if (selectedStatsIDs.length > 0 && configuredStatisticsConfigurations.length > 0) {
                // If there is a selected statistics, we should insert the new ones before it.
                const indexOfFirstSelectedElement = configuredStatisticsConfigurations
                    .filter((configuredStatistics) =>
                        selectedStatsIDs.some((selectedStatID) => selectedStatID === configuredStatistics.ID)
                    )
                    .map((selectedStat) => configuredStatisticsConfigurations.indexOf(selectedStat))
                    .sort((a, b) => a - b)[0]

                setConfiguredStatisticsConfigurations([
                    ...configuredStatisticsConfigurations.slice(0, indexOfFirstSelectedElement),
                    statisticsConfiguration,
                    ...configuredStatisticsConfigurations.slice(
                        indexOfFirstSelectedElement,
                        configuredStatisticsConfigurations.length
                    )
                ])
            } else {
                // If no statistics are selected, insert the new one at the end of the list.
                setConfiguredStatisticsConfigurations([...configuredStatisticsConfigurations, statisticsConfiguration])
            }
        }
    }

    const removeStatistic = (statProp: StatProps): void => {
        setConfiguredStatisticsConfigurations(
            configuredStatisticsConfigurations.filter(
                (statisticsConfiguration) => statisticsConfiguration.ID !== statProp.StatID
            )
        )
        setSelectedStatIDs(selectedStatsIDs.filter((selectedStatID) => selectedStatID !== statProp.StatID))
    }

    const updateStatisticsConfiguration = (updatedSelectedStatisticsConfiguration: StatisticsConfiguration): void => {
        setConfiguredStatisticsConfigurations(
            configuredStatisticsConfigurations.map((statisticsConfiguration): StatisticsConfiguration => {
                if (statisticsConfiguration.ID !== updatedSelectedStatisticsConfiguration.ID) {
                    return statisticsConfiguration
                }
                return updatedSelectedStatisticsConfiguration
            })
        )
    }

    const handleResetAll = (): void => {
        // Replace list of currently configured statistics configurations completely with initial configuration for this report.
        resetStatisticsConfigurationToReportDefault()
    }

    const handleClose = (): void => {
        setAddMenuOpen(false)
    }

    const handleSortOpen = (): void => {
        setSortMenuOpen(true)
    }

    const handleSortClose = (): void => {
        setSortMenuOpen(false)
    }

    useEffect(() => {
        toggleFieldsAddMenuOpen$
            .pipe(takeWhile((): boolean => isMounted.current))
            .subscribe((isOpen: boolean): void => setAddMenuOpen(isOpen))

        return (): void => {
            if (subscriptionForAddMenuOpen.current) {
                subscriptionForAddMenuOpen.current.unsubscribe()
            }
        }
    }, [])

    return (
        <div className='fields-tab tab-area'>
            {statsList && configuredStatisticsConfigurations && (
                <>
                    <FieldsList
                        setShowAddMenu={setAddMenuOpen}
                        openSortMenu={handleSortOpen}
                        addListItemPopoverRef={popoverRefEl}
                        sortPopoverRef={sortPopoverRef}
                        setStatsListConfiguredState={setConfiguredStatisticsConfigurations}
                        statsListConfiguredState={configuredStatisticsConfigurations}
                        handleResetAll={handleResetAll}
                        selectedStatsIDs={selectedStatsIDs}
                        setSelectedStatIDs={setSelectedStatIDs}
                        rptFilterEntityInfo={rptFilterEntityInfo}
                        isUserLicensedForCurrentReportStyle={isUserLicensedForCurrentReportStyle}
                        isSortingSupportedEver={currentReportStyleSupportsSortingEver}
                        isSortingSupportedRealTime={currentReportStyleSupportsSortingRealTime}
                        isReportRealTime={isReportRealTime}
                    />
                    <Popover
                        open={addMenuOpen}
                        anchorEl={popoverRefEl.current}
                        onClose={handleClose}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right'
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left'
                        }}
                    >
                        <FieldsListAdd
                            statsList={statsList}
                            configuredStatisticsConfigurations={configuredStatisticsConfigurations}
                            addStatistic={addStatistic}
                            removeStatistic={removeStatistic}
                            rptFilterEntityInfo={rptFilterEntityInfo}
                            isUserLicensedForCurrentReportStyle={isUserLicensedForCurrentReportStyle}
                        />
                    </Popover>

                    {!isWallboard && currentReportStyleSupportsSortingEver && (
                        <Popover
                            open={sortMenuOpen}
                            anchorEl={sortPopoverRef.current}
                            onClose={handleSortClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left'
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'left'
                            }}
                        >
                            {selectedStatisticsConfiguration && (
                                <SortListContent
                                    selectedStatisticsConfiguration={selectedStatisticsConfiguration}
                                    configuredStatisticsConfigurations={configuredStatisticsConfigurations}
                                    setConfiguredStatisticsConfigurations={setConfiguredStatisticsConfigurations}
                                    handleSortClose={handleSortClose}
                                />
                            )}
                        </Popover>
                    )}

                    <div className='fields-list-col'>
                        <CustomSettings
                            selectedStatisticsConfiguration={selectedStatisticsConfiguration}
                            updateSelectedStatisticsConfiguration={updateStatisticsConfiguration}
                            isWallboardReport={isWallboard}
                        />
                        {!isReportDisplayedAsChart &&
                            (currentReportStyleSupportsColouredStats || currentReportStyleSupportsStatDisplayedAs) && (
                                <DisplaySettings
                                    selectedStatisticsConfiguration={selectedStatisticsConfiguration}
                                    updateSelectedStatisticsConfiguration={updateStatisticsConfiguration}
                                    currencyFormatter={currencyFormatter}
                                />
                            )}
                        {statisticAlarmsSupported && (
                            <StatisticAlarm
                                selectedStatisticsConfiguration={selectedStatisticsConfiguration}
                                updateSelectedStatisticsConfiguration={updateStatisticsConfiguration}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
export default FieldsTab
